/**
 * Implements a router
 */
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { enums } from '@kattana-io/common';
import TVChartPage from './pages/TVChartPage';
import MainPage from './pages/MainPage';
import NewPairsPage from './pages/NewPairsPage';
import BigTradesPage from './pages/BigTradesPage';
import TokenPage from './pages/TokenPage';
import ChainPage from './pages/ChainPage';
import {
  tokenPageLoader,
  bigTradesPageLoader,
  newPairsPageLoader,
  cexCurrentLoader,
  dexCurrentLoader,
  directSwapsLoader,
  chainPageLoader,
  walletPageLoader,
} from './pages/loaders';
import DirectSwapPage from './pages/DirectSwap';
import useCacheBuster from './hooks/useCacheBuster';
import CompetitionPage from './pages/CompetitionPage';
import competitionLoader from './pages/loaders/competitionLoader';
import WalletPage from './pages/WalletPage';
import HelpPage from './pages/HelpPage';
import SubscriptionPage from './pages/SubscriptionPage';
import { FailedPayment, SuccessfulPayment } from './pages/PaymentPage';
import { FailedPromocode, SuccessfulPromocode } from './pages/PromocodePage';
import { DEFAULT_URL_PATH } from './helpers/constants';

const RedirectToMain = () => {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: DEFAULT_URL_PATH,
        search: location.search,
      }}
      replace={true}
    />
  );
};

const JSXRouter = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='/embed/tvchart/:chain/:address' element={<TVChartPage />} />
      <Route
        path='/top-traders/:chain/:address/:wallet/:timeframe'
        element={<MainPage />}
      />
      {/* <Route path='/wallet/:wallet/:timeframe' element={<MainPage />} /> */}
      <Route
        path='/:exchange/:base/:quote'
        element={<MainPage />}
        loader={cexCurrentLoader}
      />
      <Route
        path='/:chain/:address'
        element={<MainPage />}
        loader={dexCurrentLoader}
      />
      <Route
        path='/swap/:chain/:src/:dest'
        element={<DirectSwapPage />}
        loader={directSwapsLoader}
      />
      <Route
        path='/new-pairs'
        element={<NewPairsPage />}
        loader={newPairsPageLoader}
      />
      <Route
        path='/big-trades'
        element={<BigTradesPage />}
        loader={bigTradesPageLoader}
      />
      <Route
        path='/:lang/token/:chain/:address'
        element={<TokenPage />}
        loader={tokenPageLoader}
      />
      <Route
        path='/:lang/chain/:chain'
        element={<ChainPage />}
        loader={chainPageLoader}
      />
      <Route
        path='/competition/:exchange'
        element={<CompetitionPage />}
        loader={competitionLoader}
      />
      <Route
        path='/:lang/wallet-overview/:chain/:address'
        element={<WalletPage />}
        loader={walletPageLoader}
      />
      <Route path='/subscription' element={<SubscriptionPage />} />
      <Route path='/payment/success' element={<SuccessfulPayment />} />
      <Route path='/payment/fail' element={<FailedPayment />} />
      <Route path='/promocode/success' element={<SuccessfulPromocode />} />
      <Route path='/promocode/fail' element={<FailedPromocode />} />
      <Route path='/help' element={<HelpPage />} />
      <Route path='/dev' element={<div className='centrify'></div>} />
      <Route path='*' element={<RedirectToMain />} />
    </>
  )
);

const AuthLogic = () => {
  useCacheBuster();

  return <RouterProvider router={JSXRouter} />;
};

export const switchCurrentTicker = (payload: any) => {
  const currentRoute = JSXRouter.state.matches[0];
  const currentRoutePattern = currentRoute.route.path || '';
  const tickerRoutes = [
    '/:exchange/:base/:quote',
    '/:chain/:address',
    // '/wallet/:wallet/:timeframe',
    '/top-traders/:chain/:address/:wallet/:timeframe',
    '/:lang/token/:chain/:address',
    '/:lang/chain/:chain',
    '/competition/:exchange',
    '/:lang/wallet-overview/:chain/:address',
    '/new-pairs',
    '/big-trades',
    '/help',
    '/swap/:chain/:src/:dest',
    '/subscription',
    '/payment/success',
    '/payment/fail',
    '/promocode/success',
    '/promocode/fail',
    '/dev',
  ];
  const currentParams = currentRoute.params;
  const [newBase, newQuote] = payload.name.split('/');

  let newUrl = '';
  if (tickerRoutes.includes(currentRoutePattern)) {
    switch (payload.kind) {
    case enums.ETickerKind.CEX:
      newUrl = `/${payload.exchange}/${newBase}/${newQuote}`;
      break;
    default:
      newUrl = `/${payload.chain}/${payload.address}`;
      break;
    }
  } else {
    let newParams = currentParams;
    const { chain, address, base, quote, exchange, ...restParams } =
      currentParams;
    if (address && chain) {
      newParams = { address: payload.address, chain: payload.chain };
    } else if (base && quote && exchange) {
      newParams = {
        exchange: payload.exchange,
        base: newBase,
        quote: newQuote,
      };
    }
    newUrl = generatePath(currentRoutePattern, {
      ...newParams,
      ...restParams,
    });
  }

  return JSXRouter.navigate(newUrl);
};

export default AuthLogic;
